import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { SiteFooter } from "@/components/SiteFooter";

const PLANS = [
  { key: "weekly_1_team", label: "1 team", cadence: "weekly", price: "$1.99 / week" },
  { key: "seasonal_1_team", label: "1 team", cadence: "seasonal", price: "$25 once" },
  { key: "weekly_3_team", label: "3 teams", cadence: "weekly", price: "$3.69 / week" },
  { key: "seasonal_3_team", label: "3 teams", cadence: "seasonal", price: "$39 once" },
];

function parsePlanQuery(plan) {
  if (!plan) return null;
  return PLANS.some((p) => p.key === plan) ? plan : null;
}

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const initialPlan = parsePlanQuery(searchParams.get("plan"));
  const initialEmail = searchParams.get("email") ?? "";

  const [planKey, setPlanKey] = useState(initialPlan ?? "weekly_1_team");
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [email, setEmail] = useState(initialEmail);
  const [discountCode, setDiscountCode] = useState("");
  const [discountOpen, setDiscountOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loadingTeams, setLoadingTeams] = useState(true);

  const teamCount = planKey.endsWith("_3_team") ? 3 : 1;
  const selectedPlan = PLANS.find((p) => p.key === planKey);

  useEffect(() => {
    api
      .get("/teams")
      .then((r) => setTeams(r.data))
      .catch(() => setError("Could not load teams. Refresh and try again."))
      .finally(() => setLoadingTeams(false));
  }, []);

  // If the user shrinks plan, trim selection.
  useEffect(() => {
    if (selectedTeams.length > teamCount) {
      setSelectedTeams((prev) => prev.slice(0, teamCount));
    }
  }, [teamCount, selectedTeams.length]);

  const canContinue = useMemo(() => {
    if (selectedTeams.length !== teamCount) return false;
    if (!email || !email.includes("@")) return false;
    return !submitting;
  }, [selectedTeams.length, teamCount, email, submitting]);

  const toggleTeam = (abbr) => {
    setSelectedTeams((prev) => {
      if (prev.includes(abbr)) return prev.filter((a) => a !== abbr);
      if (prev.length >= teamCount) return prev;
      return [...prev, abbr];
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canContinue) return;
    setSubmitting(true);
    setError("");
    try {
      const body = {
        plan: planKey,
        team_abbreviations: selectedTeams,
        email,
      };
      if (discountCode.trim()) body.discount_code = discountCode.trim();
      const r = await api.post("/checkout/create-session", body);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-paper text-brand-black flex flex-col">
      <Nav />
      <main className="flex-1 pt-36 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="mb-10">
            <p className="font-mono text-[11px] font-bold tracking-[0.28em] uppercase text-brand-red">
              Sign up
            </p>
            <h1 className="mt-3 font-serif font-black text-display-md text-brand-black">
              Pick your teams
            </h1>
            <p className="mt-3 text-base md:text-lg text-brand-muted-strong">
              Under a minute to set up. Five minutes a morning to stay in the
              loop.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white border border-brand-black/20 shadow-card p-6 md:p-10 space-y-10"
          >
            <fieldset>
              <legend className="font-mono text-[11px] font-bold tracking-[0.22em] text-brand-red uppercase">
                01 · Plan
              </legend>
              <div
                role="radiogroup"
                aria-label="Plan"
                className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2"
              >
                {PLANS.map((p) => {
                  const selected = planKey === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setPlanKey(p.key)}
                      className={`text-left px-4 py-3 border transition-colors duration-200 ${
                        selected
                          ? "bg-brand-black text-white border-brand-black"
                          : "bg-white text-brand-black border-brand-black/20 hover:border-brand-red"
                      }`}
                      data-testid={`plan-${p.key}`}
                    >
                      <span className="block text-sm font-semibold">
                        {p.label}
                      </span>
                      <span
                        className={`block font-mono text-[10px] uppercase tracking-[0.12em] mt-1 ${
                          selected ? "text-brand-red" : "text-brand-muted"
                        }`}
                      >
                        {p.cadence}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p
                className="mt-3 font-mono text-sm text-brand-muted-strong"
                data-testid="price-label"
              >
                {selectedPlan?.price}
              </p>
            </fieldset>

            <fieldset>
              <div className="flex items-end justify-between gap-4">
                <legend className="font-mono text-[11px] font-bold tracking-[0.22em] text-brand-red uppercase">
                  02 · Teams
                </legend>
                <span className="font-mono text-xs text-brand-muted">
                  Selected: {selectedTeams.length}/{teamCount}
                </span>
              </div>
              {loadingTeams ? (
                <p className="mt-4 text-sm text-brand-muted">Loading teams...</p>
              ) : (
                <div
                  className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2"
                  data-testid="team-grid"
                >
                  {teams.map((t) => {
                    const selected = selectedTeams.includes(t.abbreviation);
                    const reachedLimit =
                      !selected && selectedTeams.length >= teamCount;
                    return (
                      <button
                        key={t.abbreviation}
                        type="button"
                        onClick={() => toggleTeam(t.abbreviation)}
                        disabled={reachedLimit}
                        aria-pressed={selected}
                        title={t.name}
                        className={`px-3 py-2.5 font-mono text-sm font-semibold tabular-nums border transition-colors duration-200 ${
                          selected
                            ? "bg-brand-red text-white border-brand-red"
                            : reachedLimit
                              ? "bg-white text-brand-muted-soft border-brand-black/10 cursor-not-allowed"
                              : "bg-white text-brand-black border-brand-black/20 hover:border-brand-red hover:text-brand-red"
                        }`}
                        data-testid={`team-option-${t.abbreviation}`}
                      >
                        {t.abbreviation}
                      </button>
                    );
                  })}
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend className="font-mono text-[11px] font-bold tracking-[0.22em] text-brand-red uppercase">
                03 · Email
              </legend>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-3 w-full border border-brand-black/25 bg-white px-4 py-3.5 text-base focus:outline-none focus:border-brand-red transition-colors"
                data-testid="email-input"
              />
              {!discountOpen ? (
                <button
                  type="button"
                  onClick={() => setDiscountOpen(true)}
                  className="mt-4 text-sm text-brand-red hover:text-brand-red-hover underline underline-offset-4 transition-colors"
                  data-testid="discount-toggle"
                >
                  Have a friend code?
                </button>
              ) : (
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Friend code"
                  className="mt-4 w-full border border-brand-black/25 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors"
                  data-testid="discount-input"
                  autoFocus
                />
              )}
            </fieldset>

            {error && (
              <p
                className="text-sm text-brand-red font-semibold"
                role="alert"
                data-testid="signup-error"
              >
                {error}
              </p>
            )}

            <div>
              <button
                type="submit"
                disabled={!canContinue}
                className={`w-full inline-flex items-center justify-center gap-2 px-6 py-4 font-semibold text-base transition-colors duration-200 ${
                  canContinue
                    ? "bg-brand-black text-white shadow-hard-sm hover:bg-brand-red"
                    : "bg-brand-surface text-brand-muted cursor-not-allowed"
                }`}
                data-testid="continue-button"
              >
                {submitting ? "Redirecting..." : "Continue to Checkout"}
                {!submitting && (
                  <span aria-hidden="true" className="-mr-0.5">
                    →
                  </span>
                )}
              </button>
              <p className="mt-3 font-mono text-[11px] tracking-[0.06em] text-brand-muted text-center">
                You'll be redirected to Stripe to complete payment.
              </p>
            </div>
          </form>

          <p className="mt-10 text-center text-sm text-brand-muted">
            Changed your mind?{" "}
            <Link
              to="/"
              className="text-brand-red underline underline-offset-4 hover:text-brand-red-hover transition-colors"
            >
              Back to the homepage
            </Link>
            .
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
